'use client';

import { Box, Container, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import Marquee from 'react-fast-marquee';

import { ClientOnly } from '@/components/ClientOnlyMarquee';
import { ComponentDataHomepageContent } from '@/types';

import { newHomepageTypography } from './newHomepageStyles';

type LenderSummaryItem = {
  ref: string;
  name: string;
  logo: string;
  tier: number | string | null;
};

type LenderLogo = { name: string; logo: string };

function normalizeTier(value: number | string | null | undefined): 1 | 2 | null {
  if (value == null) return null;
  if (typeof value === 'number') {
    return value === 1 || value === 2 ? value : null;
  }
  const v = value
    .toString()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (v === '1' || v === 'tier_1' || v === 'one') return 1;
  if (v === '2' || v === 'tier_2' || v === 'two') return 2;
  return null;
}

// Fixed-size cell for each logo so the row looks visually uniform regardless
// of each logo's native aspect ratio (matches the LendIQ pattern of
// `width: 120, height: 50` cells with object-fit: contain inside).
const logoSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: { xs: 110, sm: 120, md: 140 },
  height: { xs: 40, md: 52 },
  mx: { xs: 1.5, sm: 2, md: 2.5 },
  '& img': {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'contain' as const,
    // Monochrome treatment — same intent as LendIQ's
    // `filter: 'brightness(0) invert(1)'` (white logos on dark bg),
    // adapted for our white background: collapses every logo to a
    // uniform soft dark-grey so the row reads as a calm trust bar.
    filter: 'brightness(0)',
    opacity: 0.55,
  },
};

function LogoRow({
  items,
  direction,
  speed,
}: {
  items: LenderLogo[];
  direction: 'left' | 'right';
  speed: number;
}) {
  if (items.length === 0) return null;

  const renderImg = (item: LenderLogo, i: number) => (
    <Box key={`${item.name}-${i}`} sx={logoSx}>
      <Box component="img" src={item.logo} alt={`${item.name} logo`} loading="lazy" />
    </Box>
  );

  return (
    <ClientOnly
      fallback={
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            justifyContent: 'center',
            flexWrap: 'wrap',
            py: 1.5,
          }}
        >
          {items.slice(0, 8).map(renderImg)}
        </Box>
      }
    >
      <Marquee gradient={false} pauseOnHover direction={direction} speed={speed}>
        {items.map(renderImg)}
      </Marquee>
    </ClientOnly>
  );
}

function SkeletonRow() {
  const theme = useTheme();
  // Match the visual density of a real marquee row: ~7 placeholder logos
  // spread across the container with the same horizontal spacing.
  const placeholders = [110, 90, 130, 80, 120, 100, 95];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        gap: { xs: 2, md: 3 },
        py: 1,
        overflow: 'hidden',
      }}
    >
      {placeholders.map((width, i) => (
        <Box
          key={i}
          sx={{
            flexShrink: 0,
            width,
            height: { xs: 28, md: 34 },
            borderRadius: 1,
            background: `linear-gradient(90deg, ${alpha(
              theme.palette.brand.primary,
              0.06
            )} 0%, ${alpha(theme.palette.brand.primary, 0.14)} 50%, ${alpha(
              theme.palette.brand.primary,
              0.06
            )} 100%)`,
            backgroundSize: '200% 100%',
            animation: 'lendersTrustedShimmer 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.08}s`,
            '@keyframes lendersTrustedShimmer': {
              '0%': { backgroundPosition: '200% 0' },
              '100%': { backgroundPosition: '-200% 0' },
            },
          }}
        />
      ))}
    </Box>
  );
}

interface LendersTrustedSectionProps {
  cms?: ComponentDataHomepageContent | null;
}

export function LendersTrustedSection({ cms }: LendersTrustedSectionProps = {}) {
  const theme = useTheme();
  const [lenders, setLenders] = useState<LenderSummaryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const sectionLabel = cms?.LtsSectionLabel || "Trusted by Australia's best lenders";
  const headingMain = cms?.LtsHeadingMain || 'Access to';
  const headingAccent = cms?.LtsHeadingAccent || 'over 80 Lenders';
  const subtitle =
    cms?.LtsSubtitle ||
    'We compare a broad lender network so you can see stronger options, faster.';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/lenders-summary');
        const json = await res.json();
        if (!cancelled) setLenders(Array.isArray(json?.lenders) ? json.lenders : []);
      } catch (err) {
        console.error('Failed to load lenders summary:', err);
        if (!cancelled) setLenders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { row1, row2 } = useMemo(() => {
    const seen = new Set<string>();
    const r1: LenderLogo[] = [];
    const r2: LenderLogo[] = [];

    for (const l of lenders) {
      if (!l.name || !l.logo) continue;
      const key = l.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      const tier = normalizeTier(l.tier);
      if (tier === 1) r1.push({ name: l.name, logo: l.logo });
      else if (tier === 2) r2.push({ name: l.name, logo: l.logo });
    }

    return { row1: r1, row2: r2 };
  }, [lenders]);

  // Hide the section only after the fetch resolves to nothing — that way
  // we never render then disappear, and we never reflow the page.
  if (!loading && row1.length === 0 && row2.length === 0) return null;

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        background: theme.palette.common.white,
        py: { xs: 7, md: 10 },
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3.5, md: 5 } }}>
          <Typography
            sx={{
              mb: 1.1,
              ...newHomepageTypography.sectionLabel,
            }}
          >
            {sectionLabel}
          </Typography>
          <Typography
            component="h2"
            sx={{
              color: theme.palette.brand.bodyText,
              ...newHomepageTypography.h2,
            }}
          >
            {headingMain}{' '}
            <Box component="span" sx={{ color: theme.palette.brand.primary }}>
              {headingAccent}
            </Box>
          </Typography>
          <Typography
            sx={{
              maxWidth: 560,
              mx: 'auto',
              mt: 1.5,
              color: alpha(theme.palette.brand.bodyText, 0.72),
              fontSize: { xs: 15, md: 16 },
              lineHeight: 1.45,
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            // Reserve vertical space so the section height is identical
            // whether we're showing skeletons or real logos. No layout shift.
            minHeight: { xs: 110, md: 130 },
            opacity: 1,
            transition: 'opacity 0.5s ease',
            maxWidth: 1220,
            mx: 'auto',
            py: { xs: 2, md: 2.5 },
          }}
        >
          {loading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, md: 3 },
                animation: 'lendersTrustedFadeIn 0.6s ease-out both',
                '@keyframes lendersTrustedFadeIn': {
                  from: { opacity: 0, transform: 'translateY(6px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <LogoRow items={row1} direction="left" speed={40} />
              <LogoRow items={row2} direction="right" speed={35} />
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
